import { useEffect } from 'react';
import { t } from '@lingui/macro';
import { Box, Typography } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';

import NgpNativeNumberField from '../../form/NgpNativeNumberField';
import { RowFlexLayout } from '../../layout';
import { getThresholdDynamicBounds, showThresholdError } from './ThresholdValidation';

/**
 * Renders a single threshold metric row with label, Degraded/Excessive inputs,
 * units display, and inline validation error.
 *
 * Extracted from ThresholdsServiceTab for reuse across Admin Thresholds
 * and Test Detail pages.
 *
 * @param {object} props.threshold - Threshold definition { metricIds, degraded, critical, inverted, min, max, inputInteger }
 * @param {number} props.index - Array index for react-hook-form field naming (thresholds.{index}.degraded)
 * @param {function} props.getServiceMetricLabel - (metricIds) => string label
 * @param {function} [props.getServiceMetricUnit] - (metricIds) => string unit
 * @param {boolean} [props.disabled=false] - When true, inputs are disabled and validation is hidden
 */
export default function ThresholdMetricRow({
    threshold,
    index,
    getServiceMetricLabel,
    getServiceMetricUnit,
    disabled = false,
}) {
    const { watch, trigger } = useFormContext();
    const degradedFieldName = `thresholds.${index}.degraded`;
    const criticalFieldName = `thresholds.${index}.critical`;

    const degradedValue = watch(degradedFieldName);
    const criticalValue = watch(criticalFieldName);
    const unit = getServiceMetricUnit ? getServiceMetricUnit(threshold.metricIds) : '';

    const { degradedMin, degradedMax, excessiveMin, excessiveMax } = getThresholdDynamicBounds(
        threshold,
        degradedValue,
        criticalValue
    );

    // Re-validate sibling field when one changes (mirrors portal/ui's $observe on min/max)
    useEffect(() => {
        if (!disabled) {
            trigger(criticalFieldName);
        }
    }, [degradedValue, trigger, criticalFieldName, disabled]);

    useEffect(() => {
        if (!disabled) {
            trigger(degradedFieldName);
        }
    }, [criticalValue, trigger, degradedFieldName, disabled]);

    const errorMessage = disabled ? null : showThresholdError(threshold, degradedValue, criticalValue);

    return (
        <RowFlexLayout vBottom mt={2}>
            <Box style={{ minWidth: 220, textAlign: 'right' }}>
                <Typography variant="subtitle2">{getServiceMetricLabel(threshold.metricIds)}</Typography>
            </Box>
            <Box ml={4} display="flex" alignItems="flex-end" style={{ width: '25%' }}>
                <NgpNativeNumberField
                    required={!disabled}
                    fieldName={degradedFieldName}
                    label={t`Degraded`}
                    min={degradedMin}
                    max={degradedMax}
                    step="any"
                    fullWidth
                    hideError
                    disabled={disabled}
                />
                <Box style={{ minWidth: 32, marginLeft: 4 }}>
                    {unit && <Typography variant="body2">{unit}</Typography>}
                </Box>
            </Box>
            <Box ml={4} display="flex" alignItems="flex-end" style={{ width: '25%' }}>
                <NgpNativeNumberField
                    required={!disabled}
                    fieldName={criticalFieldName}
                    label={t`Excessive`}
                    min={excessiveMin}
                    max={excessiveMax}
                    step={threshold.inputInteger ? 1 : 'any'}
                    fullWidth
                    hideError
                    disabled={disabled}
                />
                <Box style={{ minWidth: 32, marginLeft: 4 }}>
                    {unit && <Typography variant="body2">{unit}</Typography>}
                </Box>
            </Box>
            <Box ml={4} style={{ width: 180 }}>
                {errorMessage && (
                    <Typography color="error" style={{ fontWeight: 'bold', fontSize: 14 }}>
                        {errorMessage}
                    </Typography>
                )}
            </Box>
        </RowFlexLayout>
    );
}
